import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { fetchGRNProductsByProductId } from "slices/Warehouse/ItemInward/GRN/thunk";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import CancelButton from "Common/Components/Buttons/CancelButton";
import { MecordColors } from "Constants/Color";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import { toast } from "react-toastify";
import LoadingSpinner from "Common/Components/LoaderErrorWrapper/LoadingSpinner";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import StatusBadge from "Common/Components/StatusBadge/StatusBadge";
import { FaArrowLeft } from "react-icons/fa6";
import "../../../../Constants/common-table.css";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import ExportButton from "Common/Components/Buttons/ExportButton";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import { SortingState } from "@tanstack/react-table";
import { fetchInStockItems } from "slices/Warehouse/ProjectWarehouse/InStockItem/thunk";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import StatusChip from "pages/Enquiry/Components/StatusChip";

const InstockItemDetails = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { warehouse_id, product_id } = useParams<any>();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch<any>();
  const [data, setData] = useState<any>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  // Table State (Aligned with instock.tsx)
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [columnFilters, setColumnFilters] = useState<any>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filterValue, setFilterValue] = useState<any[]>([]);
  const [tableInstance, setTableInstance] = useState<any>();

  useEffect(() => {
    if (warehouse_id && product_id) {
      setLoading(true);
      const params: any = {
        product_id,
        warehouse_id,
        page: pageIndex + 1,
        pageSize: pageSize,
        search: search || undefined,
        sort: sorting.length > 0 ? sorting[0].id : undefined,
        order: sorting.length > 0 ? (sorting[0].desc ? "desc" : "asc") : undefined,
        filters: filterValue,
      };

      dispatch(fetchInStockItems(params))
        .unwrap()
        .then((res: any) => {
          const fetchedData = res?.data;
          const items = Array.isArray(fetchedData)
            ? fetchedData
            : (fetchedData?.inventory_products || []);

          setData(items);
          setTotalRecords(res?.rowCount || items.length || 0);
        })
        .catch((err: any) => {
          console.error("Error:", err);
          toast.error(err?.message || "Failed to fetch item details");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [dispatch, warehouse_id, product_id, pageIndex, pageSize, search, sorting, filterValue]);

  useEffect(() => {
    setPageIndex(0);
  }, [filterValue, search, sorting]);

  // Debouncing Search  
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
      setPageIndex(0); // Reset to first page on search
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  // Local constants for display
  const displayValue = (val: any) => val || "-";

  const columns = useMemo(
    () => [
      {
        header: "Sr No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info: any) => info.row.index + 1 + pageIndex * pageSize,
      },
      {
        header: "Item Name",
        accessorKey: "item_name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => displayValue(row.original?.item_name),
      },
      {
        header: "Warranty Date",
        accessorKey: "warranty_date",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => <FormattedDate date={row.original.warranty_date} />,
      },
      {
        header: "Unit",
        accessorKey: "unit",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => displayValue(row.original?.unit),
      },
      {
        header: "Serial No.",
        accessorKey: "serial_no",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: { row: any }) => displayValue(row.original?.serial_no),
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: true,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.IN_STOCK?.DETAILS_DROPDOWN("status", warehouse_id!, product_id!),
        cell: ({ row }: { row: any }) => {
          const status = row.original?.status || (row.original?.qc === "Pass" ? "Robust" : "Damaged");
          return <StatusChip status={status} />;
          return (
            <StatusBadge
              status={status}
              bgColor={status?.toLowerCase() === "damaged" ? "#FF6C6C" : "#0CBA91"}
              color="#fff"
            />
          );
        },
      },
    ],
    [pageIndex, pageSize, warehouse_id, product_id]
  );

  return (
    <Fragment>
      <PageContainer>
        <div className="d-flex align-items-center gap-3 mb-4 mt-2">
          <FaArrowLeft
            size={20}
            style={{ cursor: "pointer" }}
            onClick={() => navigate(-1)}
          />
          <h5 className="mb-0 fw-bold" style={{ color: "#212529" }}>
            Item Details
          </h5>
        </div>

        {/* <Row className="align-items-center justify-content-between px-0 g-3 flex-wrap mb-3">
          <SearchBox
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
            placeholder="Search..."
          />

          <Col xs={12} md={12} lg={8} xl={6}>
            <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 w-100">
              <div>
                {tableInstance && (
                  <ColumnVisibilityDropdown
                    columns={tableInstance.getAllLeafColumns()}
                    excludedColumns={["actions"]}
                  />
                )}
              </div>

              <div>
                <ExportButton
                  onExport={async () => {
                    // Placeholder for export logic
                    return null;
                  }}
                />
              </div>
            </div>
          </Col>
        </Row> */}

        {loading ? (
          <LoadingSpinner />
        ) : (
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-0 mt-3">
              <LoaderErrorWrapper loading={loading} error={null}>
                <MasterTableContainer
                  columns={columns}
                  data={data}
                  totalRecords={totalRecords}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  colValue={setFilterValue}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  isBordered={false}
                  isPagination={true}
                  manualSorting={true}
                  onTableReady={setTableInstance}
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        )}

        {/* {!loading && (
          <div className="d-flex justify-content-center mt-4">
            <CancelButton
              onClick={() => navigate(-1)}
              disabled={loading}
              label="Cancel"
              showConfirmation={false}
            />
          </div>
        )} */}
      </PageContainer>
    </Fragment>
  );
};

export default InstockItemDetails;
